import {getLocale} from '@github-ui/client-env'
import {GitHubAvatar} from '@github-ui/github-avatar'
import {useRuntimeEnvironment} from '@github-ui/ssr-utils/use-runtime-environment'
import {CheckIcon} from '@primer/octicons-react'
import {Button, Link} from '@primer/react'
import type {ReactNode} from 'react'

type ProofOfPresenceUser = {
  displayName: string
  profileUrl: string
  avatarUrl: string
  avatarAlt: string
}

type ProofOfPresenceAuthentication = {
  action: string
  csrfToken: string
  pullRequestId: number
  requiredLevel?: string | null
}

export type ProofOfPresencePresentation =
  | {
      state: 'pending'
      message?: string | null
      authentication?: ProofOfPresenceAuthentication | null
    }
  | {
      state: 'completed'
      message?: string | null
      verifiedUser?: ProofOfPresenceUser | null
      verifiedAt?: string | null
      verifiedAuthenticationLevel?: string | null
    }
  | {
      state: 'error'
      message?: string | null
    }

export type ProofOfPresenceContentProps = {
  data: ProofOfPresencePresentation
  returnTo: string
}

export function ProofOfPresenceBody({data, returnTo}: ProofOfPresenceContentProps) {
  const {isBrowser} = useRuntimeEnvironment()
  let content: ReactNode

  switch (data.state) {
    case 'completed': {
      const user = data.verifiedUser
      const proofFragments = ['PoP provided']

      if (data.verifiedUser && data.verifiedAt) {
        const timestamp = new Date(data.verifiedAt)
        const formatterOptions: Intl.DateTimeFormatOptions = {
          year: 'numeric',
          month: 'short',
          day: 'numeric',
          hour: '2-digit',
          minute: '2-digit',
          timeZoneName: 'short',
          ...(isBrowser ? {} : {timeZone: 'UTC'}),
        }
        const formattedTimestamp = Number.isNaN(timestamp.getTime())
          ? data.verifiedAt
          : new Intl.DateTimeFormat(getLocale(), formatterOptions).format(timestamp)
        proofFragments.push(`at ${formattedTimestamp}`)
      }

      if (data.verifiedAuthenticationLevel === 'mfa') {
        proofFragments.push('via MFA')
      } else if (data.verifiedAuthenticationLevel === 'reauth') {
        proofFragments.push('via Re-Auth')
      }

      const label = proofFragments.join(' ')
      content = (
        <div className="d-flex flex-items-center py-1">
          <span className="d-flex flex-items-center flex-1 min-width-0 gap-1">
            {user ? (
              <Link
                href={user.profileUrl}
                className="Link--primary d-flex flex-items-start flex-1 min-width-0 gap-2 no-underline"
              >
                <span className="d-flex flex-shrink-0">
                  <GitHubAvatar src={user.avatarUrl} size={20} alt={user.avatarAlt} className="flex-shrink-0" />
                </span>
                <span className="flex-self-center min-width-0 text-small text-semibold lh-default wb-break-word">
                  {user.displayName}
                </span>
              </Link>
            ) : null}
          </span>
          <span className="d-flex flex-items-center flex-self-start gap-2 flex-shrink-0 ml-auto">
            <span role="img" aria-label={label} className="color-fg-success">
              {/* eslint-disable-next-line github/a11y-no-title-attribute -- pointer label for a noninteractive status image */}
              <span aria-hidden="true" title={label}>
                <CheckIcon size={16} />
              </span>
            </span>
          </span>
        </div>
      )
      break
    }
    case 'pending': {
      const authentication = data.authentication
      const hasNativeAuthenticationForm =
        Boolean(authentication?.action?.trim()) && Boolean(authentication?.csrfToken?.trim())
      content = (
        <>
          <p className="color-fg-muted mb-1">{data.message ?? 'An authorized user must authenticate to merge.'}</p>
          {authentication && hasNativeAuthenticationForm ? (
            <form action={authentication.action} method="post" data-turbo="false" aria-label="Authenticate to merge">
              {/* eslint-disable-next-line github/authenticity-token */}
              <input type="hidden" name="authenticity_token" value={authentication.csrfToken} />
              <input type="hidden" name="purpose" value="ref" />
              <input type="hidden" name="pull_request_id" value={String(authentication.pullRequestId)} />
              <input type="hidden" name="return_to" value={returnTo} />
              <input type="hidden" name="required_pop_level" value={authentication.requiredLevel ?? ''} />
              <Button type="submit" variant="link">
                Authenticate
              </Button>
            </form>
          ) : null}
        </>
      )
      break
    }
    case 'error':
    default:
      content = (
        <p className="color-fg-muted mb-0">
          {data.message ?? 'Unable to evaluate proof of presence. Try again later.'}
        </p>
      )
  }

  return content
}

export function ProofOfPresenceContent(props: ProofOfPresenceContentProps) {
  return (
    <section aria-label="Proof of presence">
      <div className="d-flex flex-items-center flex-justify-between">
        <h4 className="text-small text-semibold color-fg-muted m-0 min-width-0">Proof of presence</h4>
      </div>
      <div className="mt-1">
        <ProofOfPresenceBody {...props} />
      </div>
    </section>
  )
}
